import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { dateToView } from 'utils/date.utils';

// 1. สร้าง Interface สำหรับข้อมูลแต่ละแถว
export interface OverdueDetailRow {
    cnNumber: string;
    branchCnNumber: string;
    patientFullName: string;
    lastPad: string; // หรือ Date หากข้อมูลเป็น Date
    overdue: number;
}

// 2. สร้าง Interface สำหรับ Report ทั้งหมด
export interface OverdueDetailReport {
    title: string;
    branchName: string;
    dateStart: string;
    dateEnd: string;
    rows: OverdueDetailRow[];
    t: any; //ฟังก์ชันสำหรับแปลภาษา
}

// 3. ปรับปรุงฟังก์ชัน export ให้รับ report object ตัวเดียว
export async function exportOverdueDetailToExcel(report: OverdueDetailReport) {
    // สร้าง workbook และ worksheet
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet(report.title, {
        pageSetup: { orientation: 'portrait' }
    });
    ws.properties.defaultRowHeight = 20;

    // กำหนดความกว้างของคอลัมน์
    ws.columns = [
        { width: 8 },   // No.
        { width: 18 },  // CN Number
        { width: 16 },  // Branch
        { width: 24 },  // Customer Name
        { width: 14 },  // Last Paid
        { width: 16 },  // Overdue Amount
    ];

    // ExcelJS จะสร้าง header row มาให้ก่อน 1 แถว เราลบออกไปก่อน
    ws.spliceRows(1, 1);

    let rowIndex = 1;

    // เพิ่มหัวข้อหลัก (Title)
    ws.mergeCells(rowIndex, 1, rowIndex, 6); // Merge 6 คอลัมน์
    const titleCell = ws.getCell(rowIndex, 1);
    titleCell.value = report.title;
    titleCell.font = { size: 16, bold: true };
    titleCell.alignment = { horizontal: 'left', vertical: 'middle' };
    rowIndex++;

    // เพิ่มข้อมูลตัวกรอง (Filters)
    const filterText = `${report.t('REPORT.TABLE.DECREASE.BRANCH')}: ${report.branchName || '-'}   ${report.t('REPORT.TABLE.DECREASE.DATE_START')}: ${report.dateStart ? dateToView(report.dateStart) : '-'}   ${report.t('REPORT.TABLE.DECREASE.DATE_END')}: ${report.dateEnd ? dateToView(report.dateEnd) : '-'}`;
    ws.mergeCells(rowIndex, 1, rowIndex, 6);
    const filterCell = ws.getCell(rowIndex, 1);
    filterCell.value = filterText;
    filterCell.font = { size: 11 };
    filterCell.alignment = { horizontal: 'left', vertical: 'middle' };
    rowIndex += 2; // เว้นบรรทัด

    // สร้าง Header ของตาราง
    const headers = [
        report.t('TABLE.NO'),
        report.t('APPLICATION.TABLE.CELL.CN_NUMBER'),
        report.t('PATIENT_MANAGEMENT.TABLE.CELL.BRANCH_ID'),
        report.t('OVERDUE_DETAIL.TABLE.NAME_CUSTOMER'),
        report.t('OVERDUE_DETAIL.TABLE.PAY_LASTED'),
        report.t('OVERDUE_DETAIL.TABLE.SUM_PAYED'),
    ];
    const headerRow = ws.getRow(rowIndex);
    headerRow.values = headers;
    headerRow.height = 24;
    headerRow.font = { bold: true };

    headerRow.eachCell((cell, colNumber) => {
        // จัดตำแหน่งและใส่สไตล์ให้ Header
        if ([1, 2].includes(colNumber)) {
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
        } else if ([3, 4, 5].includes(colNumber)) {
            cell.alignment = { horizontal: 'left', vertical: 'middle' };
        } else {
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
        }

        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFFFFF' } // สีเทาอ่อน
        };
        cell.border = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        };
    });
    rowIndex++;


    // จัดการกรณีไม่มีข้อมูล
    if (!report.rows || report.rows.length === 0) {
        const noDataRow = ws.getRow(rowIndex);
        noDataRow.values = [report.t('REPORT.TITLE.NO_DATA')];
        ws.mergeCells(rowIndex, 1, rowIndex, headers.length);
        noDataRow.font = { italic: true };
        noDataRow.alignment = { horizontal: 'center' };
    } else {
        // เติมข้อมูลแต่ละแถว
        report.rows.forEach((r, i) => {
            const dataRow = ws.getRow(rowIndex + i);
            dataRow.values = [
                i + 1,
                r.cnNumber || '-',
                r.branchCnNumber || '-',
                r.patientFullName || '-',
                dateToView(r.lastPad),
                r.overdue
            ];
            dataRow.height = 20;

            dataRow.eachCell((cell, colNumber) => {
                // ใส่เส้นขอบ
                cell.border = {
                    top: { style: 'thin' }, left: { style: 'thin' },
                    bottom: { style: 'thin' }, right: { style: 'thin' }
                };

                // จัดตำแหน่งและ Format ตามคอลัมน์
                switch (colNumber) {
                    case 1: // No.
                    case 2: // CN Number
                        cell.alignment = { horizontal: 'center', vertical: 'middle' };
                        break;
                    case 3: // Branch
                    case 4: // Customer Name
                    case 5: // Last Paid
                        cell.alignment = { horizontal: 'left', vertical: 'middle' };
                        break;
                    case 6: // Overdue Amount
                        cell.alignment = { horizontal: 'right', vertical: 'middle' };
                        cell.numFmt = '#,##0.00'; // จัดรูปแบบตัวเลขใน Excel
                        break;
                }
            });
        });
    }

    // สร้างไฟล์และดาวน์โหลด
    const buffer = await wb.xlsx.writeBuffer();
    saveAs(
        new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `${report.title}_${moment().format('YYYYMMDD')}.xlsx`
    );
}

